import React, {useState, useMemo, useEffect} from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import PropTypes from 'prop-types'
import {Trans, useTranslation} from 'react-i18next'
import {
  Text,
  Button,
  Flex,
  Box,
  Skeleton,
  Stack,
  Checkbox,
  Link,
  RadioGroup,
  useToast,
} from '@chakra-ui/react'
import {camelizeResult, dineroMoneyObjects} from '../../../../api/serializer'
import {useRails} from '../../../contexts/rails'
import {fetcher, defaultHeaders} from '../../../../api'
import {SupportAgentIcon} from '../../../icons'
import {BankAccountCard} from '../components'
import IconTooltip from '../../IconTooltip'
import DirectDebitRequestServiceAgreementModal from '../../ZeptoDirectDebitRequestModals/DirectDebitRequestServiceAgreementModal'
import DirectDebitRequestModal from '../../ZeptoDirectDebitRequestModals/DirectDebitRequestModal'

const SelectPayoutAccountScreen = ({
  goToConfirmation,
  goToReconciliationAccount,
  onClose,
}) => {
  const {t} = useTranslation('connectPayoutAccount')
  const {organisation, authenticityToken} = useRails()
  const toast = useToast()

  const BANK_ACCOUNT_ENDPOINT = `/api/suppliers/${organisation.currentSupplier.id}/banking_aggregators/bank_accounts`
  const SELECT_ENDPOINT = `/api/suppliers/${organisation.currentSupplier.id}/banking_aggregators/payout_accounts`

  const {data: bankAccountData, isLoading} = useSWR(
    BANK_ACCOUNT_ENDPOINT,
    fetcher,
    {
      use: [camelizeResult, dineroMoneyObjects],
      refreshInterval: 5000,
    },
  )

  const selectBankAccount = async (url, {arg}) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: defaultHeaders({authenticityToken}),
      body: JSON.stringify({
        payout_account: {
          account_number: arg.accountNumber,
          direct_debit_authorization: arg.directDebitAuthorization,
        },
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      const error = new Error(
        data?.error?.message || 'Failed to select bank account',
      )
      error.info = data
      error.status = response.status
      throw error
    }

    return data
  }

  const {trigger, isMutating} = useSWRMutation(
    SELECT_ENDPOINT,
    selectBankAccount,
  )

  const bankAccounts = useMemo(
    () => bankAccountData?.accounts || [],
    [bankAccountData?.accounts],
  )

  const [selectedAccount, setSelectedAccount] = useState('')
  const [directDebitAuthorization, setDirectDebitAuthorization] =
    useState(false)

  const hasSingleAccount = bankAccounts && bankAccounts.length === 1
  const hasMultipleAccounts = bankAccounts && bankAccounts.length > 1

  // Auto-select single account
  useEffect(() => {
    if (hasSingleAccount && !selectedAccount) {
      setSelectedAccount(bankAccounts[0].id)
    }
  }, [hasSingleAccount, bankAccounts, selectedAccount])

  const handleContinue = async () => {
    if (!selectedAccount || !directDebitAuthorization) return

    const account = bankAccounts.find(({id}) => id === selectedAccount)
    if (!account) return

    try {
      const result = await trigger({
        accountNumber: account.accountNumber,
        directDebitAuthorization,
      })

      if (result?.success) {
        if (result?.supplier?.bank_account_id) {
          goToConfirmation()
        } else {
          goToReconciliationAccount(result.supplier)
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error.message || 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <Flex direction="column" height="100%" overflow="hidden">
      <Box
        overflowY="auto"
        flex="1"
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
        }}
      >
        <Flex
          flexDirection="column"
          width="400px"
          gap={5}
          pt={10}
          pb={5}
          mx="auto"
        >
          <Text
            color="black"
            fontWeight="regular"
            fontSize="2xl"
            lineHeight="90%"
          >
            {t('modal.selectPayoutAccount.title')}
          </Text>

          <Text
            color="black"
            fontWeight="medium"
            fontSize="14px"
            lineHeight="125%"
          >
            {t('modal.selectPayoutAccount.description')}
          </Text>

          {(isLoading || !bankAccounts || bankAccounts.length === 0) && (
            <Stack gap="sm">
              <Skeleton height="80px" borderRadius="lg" />
              <Skeleton height="80px" borderRadius="lg" />
            </Stack>
          )}

          {hasSingleAccount && (
            <Stack gap="sm">
              <BankAccountCard
                key={bankAccounts[0].id}
                accountName={bankAccounts[0].accountName}
                accountNumber={bankAccounts[0].accountNumber}
                providerName={bankAccounts[0].providerName}
                isDisplay
              />
            </Stack>
          )}

          {hasMultipleAccounts && (
            <RadioGroup value={selectedAccount} onChange={setSelectedAccount}>
              <Stack gap="sm">
                {bankAccounts.map(
                  ({id, accountName, accountNumber, providerName}) => (
                    <BankAccountCard
                      key={id}
                      value={id}
                      accountName={accountName}
                      accountNumber={accountNumber}
                      providerName={providerName}
                      isSelected={selectedAccount === id}
                      onSelect={() => setSelectedAccount(id)}
                    />
                  ),
                )}
              </Stack>
            </RadioGroup>
          )}

          <Box>
            <Checkbox
              isChecked={directDebitAuthorization}
              onChange={(e) => setDirectDebitAuthorization(e.target.checked)}
              variant="mld"
              sx={{
                '.chakra-checkbox__control': {
                  _hover: {
                    borderColor: 'mld.neutral.900',
                  },
                },
              }}
            >
              <Text textStyle="body-small" color="mld.neutral.900">
                {t('modal.selectPayoutAccount.checkbox.label')}
                <IconTooltip
                  title={t('modal.selectPayoutAccount.checkbox.tooltip.title')}
                >
                  <Text textStyle="body-small">
                    {t('modal.selectPayoutAccount.checkbox.tooltip.body')}
                  </Text>
                </IconTooltip>
              </Text>
            </Checkbox>
            <Flex
              pl={7}
              mt="xs"
              flexDirection="row"
              alignItems="center"
              gap="xxs"
            >
              <Text fontSize="xs">
                <DirectDebitRequestServiceAgreementModal
                  triggerLabel={t(
                    'modal.selectPayoutAccount.directDebitRequest',
                  )}
                />
              </Text>
              <Text fontSize="xs">{t('modal.selectPayoutAccount.and')}</Text>
              <Text fontSize="xs">
                <DirectDebitRequestModal
                  triggerLabel={t(
                    'modal.selectPayoutAccount.directDebitRequestServiceAgreement',
                  )}
                />
              </Text>
            </Flex>
          </Box>

          <Flex
            flexDirection="column"
            align="flex-start"
            p="sm"
            bg="mld.neutral.50"
            borderRadius="4px"
          >
            <Flex gap={2}>
              <SupportAgentIcon />
              <Text fontSize="xs" fontWeight="bold" mb="xs" lineHeight="125%">
                {t('modal.selectPayoutAccount.changeAccountLater.title')}
              </Text>
            </Flex>
            <Flex>
              <Text fontSize="xs" lineHeight="125%">
                <Trans
                  t={t}
                  i18nKey="modal.selectPayoutAccount.changeAccountLater.description"
                  components={{
                    a: (
                      <Link
                        href="mailto:support@withmarmalade.com"
                        target="_blank"
                        textDecoration="underline"
                      />
                    ),
                  }}
                />
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex pb="lg" gap="sm" maxW="400px" mx="auto">
          <Button
            variant="primary"
            size="md"
            onClick={handleContinue}
            isDisabled={!selectedAccount || !directDebitAuthorization}
            isLoading={isMutating}
            loadingText={t('modal.buttons.continue')}
          >
            {t('modal.buttons.continue')}
          </Button>
          <Button
            borderRadius="full"
            variant="secondary"
            width="120px"
            onClick={onClose}
            _hover={{}}
          >
            {t('modal.buttons.cancel')}
          </Button>
        </Flex>
      </Box>
    </Flex>
  )
}

SelectPayoutAccountScreen.propTypes = {
  goToConfirmation: PropTypes.func.isRequired,
  goToReconciliationAccount: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default SelectPayoutAccountScreen
